import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { User, Shield, ArrowLeft, Camera, Loader2 } from 'lucide-react';
import { compressImage, formatFileSize } from '../utils/imageCompression';
import { AchievementCard, AchievementBadge } from '../components/AchievementBadge';
import { getAchievementLevel } from '../utils/achievements';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [compressionInfo, setCompressionInfo] = useState(null);
  const [formData, setFormData] = useState({
    display_name: user?.display_name || '',
    privacy_setting: user?.privacy_setting || 'full_name',
  });

  if (!user) {
    navigate('/login');
    return null;
  }

  const level = getAchievementLevel(user.days_fasted || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.display_name.trim()) {
      toast.error('Display name is required');
      return;
    }

    setLoading(true);
    try {
      await updateUser(formData);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB for input)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingPicture(true);
    setCompressionInfo(null);

    try {
      // Compress the image
      const originalSize = file.size;
      const compressed = await compressImage(file);
      
      // Show compression info
      const savings = ((originalSize - compressed.size) / originalSize * 100).toFixed(0);
      setCompressionInfo({
        original: formatFileSize(originalSize),
        compressed: formatFileSize(compressed.size),
        savings: savings
      });

      // Update profile with compressed image
      await updateUser({ profile_picture: compressed.base64 });
      toast.success(`Profile picture updated! (Compressed ${savings}%)`);
    } catch (error) {
      console.error('Image compression error:', error);
      toast.error('Failed to process image. Please try a different one.');
    } finally {
      setUploadingPicture(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-12 px-4" data-testid="profile-page">
      <div className="grain-overlay" />
      
      <div className="max-w-2xl mx-auto animate-fade-in-up">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="space-y-6">
          {/* Profile Info Card with Picture */}
          <Card className="bg-white/60 backdrop-blur-sm border-border/50">
            <CardHeader>
              <div className="flex items-center gap-6">
                {/* Profile Picture with Achievement Badge */}
                <div className="relative group">
                  <button
                    onClick={handlePictureClick}
                    disabled={uploadingPicture}
                    className={`relative w-24 h-24 rounded-full overflow-hidden flex items-center justify-center transition-all group-hover:ring-4 group-hover:ring-opacity-30 ${level.color.replace('bg-', 'group-hover:ring-')}`}
                    style={{ backgroundColor: user.profile_picture ? 'transparent' : `hsl(var(--primary) / 0.1)` }}
                    data-testid="profile-picture-btn"
                  >
                    {uploadingPicture ? (
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    ) : user.profile_picture ? (
                      <img 
                        src={user.profile_picture} 
                        alt={user.display_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-primary font-display text-3xl">
                        {user.display_name.charAt(0).toUpperCase()}
                      </span>
                    )}
                    
                    {/* Overlay on hover */}
                    {!uploadingPicture && (
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Camera className="h-6 w-6 text-white" />
                      </div>
                    )}
                  </button>
                  
                  {/* Achievement Badge on Profile Picture */}
                  <div className="absolute -bottom-2 -right-2">
                    <AchievementBadge daysFasted={user.days_fasted || 0} size="md" />
                  </div>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="profile-picture-input"
                  />
                </div>

                <div className="flex-1">
                  <CardTitle className="font-display text-2xl">{user.display_name}</CardTitle>
                  <CardDescription>{user.email}</CardDescription>
                  
                  {/* Achievement Level Display */}
                  <div className={`flex items-center gap-2 mt-3 ${level.textColor}`}>
                    <span className="font-medium">{level.name}</span>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-muted-foreground text-sm">{level.description}</span>
                  </div>
                </div>
              </div>
            </CardHeader>
            
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Click on your profile picture to upload a new one. Images are automatically compressed for optimal performance.
              </p>
              {compressionInfo && (
                <p className="text-xs text-primary mt-2">
                  Image compressed: {compressionInfo.original} → {compressionInfo.compressed} ({compressionInfo.savings}% smaller)
                </p>
              )}
            </CardContent>
          </Card>

          {/* Achievement Progress Card */}
          <AchievementCard daysFasted={user.days_fasted || 0} />

          {/* Edit Profile Card */}
          <Card className="bg-white/60 backdrop-blur-sm border-border/50">
            <CardHeader>
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Edit Profile
              </CardTitle>
            </CardHeader>
            
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="display_name">Display Name</Label>
                  <Input
                    id="display_name"
                    value={formData.display_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                    placeholder="Your name"
                    data-testid="profile-display-name"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-primary hover:bg-primary/90 text-white rounded-full"
                  data-testid="profile-save"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </CardContent>
            </form>
          </Card>

          {/* Privacy Settings Card */}
          <Card className="bg-white/60 backdrop-blur-sm border-border/50">
            <CardHeader>
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Privacy Settings
              </CardTitle>
              <CardDescription>
                Control how your name appears in comments and encouragements
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <RadioGroup
                value={formData.privacy_setting}
                onValueChange={(value) => setFormData(prev => ({ ...prev, privacy_setting: value }))}
                className="space-y-3"
              >
                <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary/50 transition-colors">
                  <RadioGroupItem value="full_name" id="full_name" data-testid="privacy-full-name" />
                  <Label htmlFor="full_name" className="flex-1 cursor-pointer">
                    <span className="font-medium">Full name</span>
                    <p className="text-sm text-muted-foreground">Display your full name (e.g., John Smith)</p>
                  </Label>
                </div>
                
                <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary/50 transition-colors">
                  <RadioGroupItem value="first_name" id="first_name" data-testid="privacy-first-name" />
                  <Label htmlFor="first_name" className="flex-1 cursor-pointer">
                    <span className="font-medium">First name only</span>
                    <p className="text-sm text-muted-foreground">Only show your first name (e.g., John)</p>
                  </Label>
                </div>
                
                <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary/50 transition-colors">
                  <RadioGroupItem value="anonymous" id="anonymous" data-testid="privacy-anonymous" />
                  <Label htmlFor="anonymous" className="flex-1 cursor-pointer">
                    <span className="font-medium">Anonymous</span>
                    <p className="text-sm text-muted-foreground">Appear as "Anonymous" in comments</p>
                  </Label>
                </div>
              </RadioGroup>

              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="mt-6 bg-primary hover:bg-primary/90 text-white rounded-full"
                data-testid="privacy-save"
              >
                {loading ? 'Saving...' : 'Update Privacy Settings'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Profile;
